import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { Play, ArrowRight, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function VendorPartnershipVideo() {
  const [meta, setMeta] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/vendor-video`, { timeout: 8000 });
        if (!cancelled && r.data?.available) setMeta(r.data);
      } catch {
        // Render fails silently — section still shows poster + apply CTA.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handlePlay = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = false;
    setMuted(false);
    const p = videoRef.current.play();
    if (p && typeof p.catch === "function") p.catch(() => {});
    setPlaying(true);
  };

  const toggleMute = () => {
    if (!videoRef.current) return;
    videoRef.current.muted = !videoRef.current.muted;
    setMuted(videoRef.current.muted);
  };

  const videoUrl = meta?.url ? `${BACKEND_URL}${meta.url}` : null;

  return (
    <section
      data-testid="vendor-partnership-video"
      className="relative overflow-hidden brand-gradient"
    >
      {/* Soft Nodera-brand hero blobs (matches the rest of the site) */}
      <span className="hero-blob bg-blue-200 top-[-120px] left-[-80px] w-[440px] h-[440px]" aria-hidden />
      <span className="hero-blob bg-indigo-100 bottom-[-160px] right-[-160px] w-[480px] h-[480px] opacity-50" aria-hidden />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          {/* Left: copy */}
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs font-medium text-slate-700 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600 pulse-dot" /> Vendor partnership film
            </div>
            <h2 className="mt-6 font-display font-extrabold tracking-tighter text-slate-900 text-4xl sm:text-5xl lg:text-[56px] leading-[1.04]">
              The infrastructure layer{" "}
              <span className="bg-gradient-to-r from-slate-900 via-blue-700 to-slate-900 bg-clip-text text-transparent">
                connecting modern healthcare
              </span>
              .
            </h2>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-md">
              A 2-minute look at why diagnostic labs, pharmacies, monitoring providers,
              and digital health vendors are joining the Nodera Vendor Network.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#apply"
                data-testid="vendor-video-apply-cta"
                className="group inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 text-sm font-bold tracking-tight transition-colors shadow-lg shadow-blue-600/25"
              >
                Apply to join the Nodera Vendor Network
                <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
              </a>
              {!playing && videoUrl && (
                <button
                  type="button"
                  onClick={handlePlay}
                  data-testid="vendor-video-play-cta"
                  className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-slate-50 border border-slate-200 px-5 py-3 text-sm font-semibold text-slate-900 transition-colors shadow-sm"
                >
                  <Play className="w-4 h-4 fill-blue-600 text-blue-600" />
                  Watch the film
                </button>
              )}
            </div>

            {/* Stats strip */}
            <dl className="mt-10 grid grid-cols-3 gap-6 max-w-md text-left">
              {[
                ["Recurring", "utilization"],
                ["Standardized", "onboarding"],
                ["Operational", "scorecards"],
              ].map(([a, b], i) => (
                <div key={i}>
                  <dt className="text-[10px] uppercase tracking-[0.18em] font-bold text-blue-700">
                    {a}
                  </dt>
                  <dd className="mt-1 text-sm text-slate-600 leading-tight">{b}</dd>
                </div>
              ))}
            </dl>
          </div>

          {/* Right: video */}
          <div className="lg:col-span-7">
            <div
              className="relative rounded-3xl overflow-hidden bg-white border border-slate-200 shadow-2xl shadow-blue-900/10"
              style={{ aspectRatio: "16 / 9" }}
            >
              {videoUrl ? (
                <>
                  <video
                    ref={videoRef}
                    src={videoUrl}
                    className="w-full h-full object-cover bg-slate-50"
                    playsInline
                    autoPlay
                    muted={muted}
                    loop
                    preload="metadata"
                    data-testid="vendor-video-player"
                  />
                  {/* Tap overlay to unmute / launch full audio play */}
                  {!playing && (
                    <button
                      type="button"
                      onClick={handlePlay}
                      data-testid="vendor-video-overlay-play"
                      className="absolute inset-0 flex items-center justify-center group"
                      style={{ background: "linear-gradient(180deg, rgba(15,23,42,0) 0%, rgba(15,23,42,0.18) 60%, rgba(15,23,42,0.55) 100%)" }}
                    >
                      <span className="flex items-center gap-3 rounded-full bg-blue-600 group-hover:bg-blue-700 text-white px-7 py-3.5 text-sm font-bold shadow-xl shadow-blue-900/30 transition-colors">
                        <Play className="w-4 h-4 fill-white" />
                        Play with sound
                      </span>
                    </button>
                  )}
                  {/* Mute toggle (always visible while playing) */}
                  {playing && (
                    <button
                      type="button"
                      onClick={toggleMute}
                      data-testid="vendor-video-mute-toggle"
                      className="absolute bottom-4 right-4 rounded-full bg-white/95 hover:bg-white text-slate-900 p-2.5 ring-1 ring-slate-200 shadow-lg"
                      aria-label={muted ? "Unmute" : "Mute"}
                    >
                      {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                    </button>
                  )}
                </>
              ) : (
                /* Pre-render placeholder — Nodera-brand light tone */
                <div
                  data-testid="vendor-video-placeholder"
                  className="w-full h-full flex flex-col items-center justify-center text-center p-8 brand-gradient"
                >
                  <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-blue-700 mb-4">
                    Coming Shortly
                  </div>
                  <div className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter text-slate-900 max-w-md">
                    The vendor partnership film is rendering.
                  </div>
                  <div className="mt-3 text-sm text-slate-500 max-w-sm">
                    Refresh in a few minutes — or apply now and we'll send you a private screening link.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
